import { Component, OnInit, OnDestroy, ViewEncapsulation } from '@angular/core';
import { TitleCasePipe } from '@angular/common';
import { ChatService, ITypedFile, OrganizationService, ToastService, NotificationService } from '../../services';
import { PropertyStateService } from '../../state/property-state.service';
import * as Parse from 'parse';

@Component({
    selector: 'app-chat-sidemenu',
    templateUrl: './chat-sidemenu.component.html',
    styleUrls: ['./chat-sidemenu.component.scss'],
    encapsulation: ViewEncapsulation.ShadowDom
})
export class ChatSidemenuComponent implements OnInit, OnDestroy {
    public displayedMessages: any[] = [];
    public messageThread: Parse.Object | undefined;
    private messages: Parse.Object[] = [];
    private messageSubscription: Parse.LiveQuerySubscription | undefined;
    private isChatOpen: boolean = false;

    constructor(
        public state: PropertyStateService,
        public orgService: OrganizationService,
        public chatService: ChatService,
        private titleCase: TitleCasePipe,
        private toast: ToastService,
        private notificationService: NotificationService
    ) {}

    async ngOnInit() {
        // Wait for propertyId and customerId to be set, then setup subscription
        // Note: Notification permission is now requested at app startup in app.component.ts
        this.waitForChatServiceAndSetupSubscription();
    }

    /**
     * Wait for chatService to have propertyId and customerId, then setup subscription
     */
    private async waitForChatServiceAndSetupSubscription() {
        const maxAttempts = 50; // 5 seconds max
        let attempts = 0;

        const checkInterval = setInterval(async () => {
            attempts++;

            if (this.chatService.propertyId && this.chatService.customerId) {
                clearInterval(checkInterval);
                await this.setupMessageSubscription();
            } else if (attempts >= maxAttempts) {
                clearInterval(checkInterval);
            }
        }, 100);
    }

    async onWillOpen() {
        // If no thread exists yet, create it
        if (!this.messageThread) {
            try {
                this.messageThread = (await this.chatService.fetchMessageThread(this.chatService.propertyId, this.chatService.customerId)) as Parse.Object;
            } catch (error: any) {
                console.error(error);
                return this.toast.showError('We could not load the message thread for this property.');
            }

            if (!this.messageThread) {
                try {
                    this.messageThread = await this.chatService.createMessageThread(this.chatService.propertyId, this.chatService.customerId);
                    // Setup subscription for newly created thread
                    await this.setupMessageSubscription();
                } catch (error: any) {
                    console.error(error);
                    return this.toast.showError('We could not create the message thread for this property.');
                }
            }
        }

        // Load messages if we don't have them yet
        if (this.messages.length === 0 && this.messageThread) {
            try {
                this.messages = await this.chatService.loadMessagesByThread(this.messageThread);
            } catch (error: any) {
                console.error(error);
                return this.toast.showError('We could not load the chat messages for this property.');
            }
        }

        // Display messages
        this.displayedMessages = this.messages.map((message: Parse.Object) => {
            return {
                text: message.get('message'),
                reply: message.get('from') === 'customer',
                date: message.createdAt,
                type: message.get('files').length === 0 ? 'text' : 'file',
                files: message.get('files').map((item: ITypedFile) => {
                    return {
                        url: item.file.url(),
                        type: item.type,
                        icon: 'file-text-outline'
                    }
                }),
                user: {
                    name: message.get('from') === 'user' ? this.messageThread?.get('agentName') : this.titleCase.transform(this.messageThread?.get('customerName'))
                },
            };
        });
    }

    async onDidOpen() {
        this.isChatOpen = true;
        if (this.messageThread) {
            try {
                await this.chatService.updateLastReadDate(this.messageThread, 'customer');
            } catch (error: any) {
                console.error(error);
            }
        }
    }

    async onDidClose() {
        this.isChatOpen = false;
        // Don't unsubscribe - we want to keep listening for notifications
    }

    ngOnDestroy() {
        // Clean up subscription when component is destroyed
        if (this.messageSubscription) {
            this.messageSubscription.unsubscribe();
        }
    }

    /**
     * Setup message subscription for notifications
     */
    private async setupMessageSubscription() {
        if (!this.chatService.propertyId || !this.chatService.customerId) {
            return;
        }

        try {
            // Try to load existing message thread
            this.messageThread = (await this.chatService.fetchMessageThread(
                this.chatService.propertyId,
                this.chatService.customerId
            )) as Parse.Object;

            if (!this.messageThread) {
                return;
            }

            // Subscribe to messages
            this.messageSubscription = await this.chatService.subscribeToMessages(this.messageThread);

            this.messageSubscription.on('create', (object: Parse.Object) => {
                // Show notification if message is from agent and (chat is closed OR tab is not focused)
                if (object.get('from') === 'user' && (!this.isChatOpen || !this.notificationService.isDocumentFocused())) {
                    this.notificationService.showChatNotification({
                        threadId: this.messageThread?.id || '',
                        senderName: this.messageThread?.get('agentName') || 'Agent',
                        messagePreview: object.get('message'),
                        propertyAddress: this.chatService.address,
                        timestamp: object.createdAt
                    });
                }

                // If chat is open, add to displayed messages
                if (this.isChatOpen) {
                    this.messages.push(object);
                    this.displayedMessages = [
                        ...this.displayedMessages,
                        {
                            text: object.get('message'),
                            reply: object.get('from') === 'customer',
                            date: object.createdAt,
                            type: object.get('files').length === 0 ? 'text' : 'file',
                            files: object.get('files').map((item: ITypedFile) => {
                                return {
                                    url: item.file.url(),
                                    type: item.type,
                                    icon: 'file-text-outline'
                                }
                            }),
                            user: {
                                name: object.get('from') === 'user' ? this.messageThread?.get('agentName') : this.titleCase.transform(this.messageThread?.get('customerName'))
                            },
                        }
                    ];
                }
            });
        } catch (error: any) {
            console.error('Error setting up message subscription:', error);
        }
    }

    async sendMessage(event: any) {
        let parseFiles: Parse.File[] = [];
        let typedFiles: ITypedFile[] = [];

        if (event.files) {
            try {
                parseFiles = await this.chatService.saveFiles(event.files);
                typedFiles = parseFiles.map((file: Parse.File) => {
                    return {
                        type: file.tags().type,
                        file: file
                    };
                });
            } catch (error: any) {
                console.error(error);
                return this.toast.showError('We could not upload your file attachments.');
            }
        }

        try {
            await this.chatService.createMessage(this.messageThread as Parse.Object, event.message, 'customer', typedFiles);
        } catch (error: any) {
            console.error(error);
            void this.toast.showError('We could not send your message.');
        }

        try {
            await this.chatService.updateLastReadDate(this.messageThread as Parse.Object, 'customer');
        } catch (error: any) {
            console.error(error);
        }
    }
}
